import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ConfigurationService } from '@pflegix/config';
import { HttpClientService } from '@pflegix/http-client';
import type {
  CreateDocumentationDTO,
  DocumentationPopulatedResponseDto,
  DocumentationResponseDto,
  ICaregiverDocumentationCount,
  MissingSignatureBillingExceptionDto,
  PaginateResultDto,
  UpdateDocumentationDTO,
} from '@pflegix/types';
import { lastValueFrom, Observable } from 'rxjs';

enum DocumentationTopic {
  GET_DOCUMENTATION = 'GET_DOCUMENTATION',
  GET_DOCUMENTATION_POPULATED = 'GET_DOCUMENTATION_POPULATED',
  GET_DOCUMENTATIONS_PAGINATED = 'GET_DOCUMENTATIONS_PAGINATED',
}

@Injectable({ providedIn: 'root' })
export class DocumentationApi {
  private readonly config = inject(ConfigurationService);
  private readonly httpClient = inject(HttpClientService);

  private get apiUrl(): string {
    return `${this.config.get('basePflegixApiUrl')}/api/documentations`;
  }

  public getUnpopulated(documentationId: string): Promise<DocumentationResponseDto> {
    return lastValueFrom(
      this.httpClient.get<DocumentationResponseDto>(
        `${this.apiUrl}/${documentationId}/unpopulated`,
        this.httpClient.buildResilienceConfigForTopic(DocumentationTopic.GET_DOCUMENTATION)
      )
    );
  }

  /**
   * Returns a specific populated entity of type T referenced by id.
   * @param id the id of the enity to grab from the db.
   */
  public getPopulated(documentationId: string): Promise<DocumentationPopulatedResponseDto> {
    return lastValueFrom(
      this.httpClient.get<DocumentationPopulatedResponseDto>(
        `${this.apiUrl}/${documentationId}`,
        this.httpClient.buildResilienceConfigForTopic(DocumentationTopic.GET_DOCUMENTATION_POPULATED)
      )
    );
  }

  /**
   * @deprecated DO NOT USE!!! Use smaller, more specific update endpoints!!!
   *
   * @param {string} id
   * @param {UpdateDocumentationDTO} updateDocumentationDTO
   * @return {Observable<DocumentationResponseDto>}
   * @memberof DocumentationApi
   */
  public update(id: string, updateDocumentationDTO: UpdateDocumentationDTO): Observable<DocumentationResponseDto> {
    return this.httpClient.patch<DocumentationResponseDto>(`${this.apiUrl}/${id}`, updateDocumentationDTO);
  }

  public create(createDocumentationDTO: CreateDocumentationDTO): Observable<DocumentationResponseDto> {
    return this.httpClient.post<DocumentationResponseDto>(`${this.apiUrl}/`, createDocumentationDTO);
  }

  public updateMissingSignatureBillingException(
    documentationId: string,
    missingSignatureBillingExceptionDTO: MissingSignatureBillingExceptionDto
  ): Observable<DocumentationResponseDto> {
    return this.httpClient.patch<DocumentationResponseDto>(
      `${this.apiUrl}/${documentationId}/performance-proof/missing-signature-billing-exception`,
      { missingSignatureBillingException: missingSignatureBillingExceptionDTO }
    );
  }

  /**
   * Retrieves a paginated list of leads.
   *
   * @param page - The page number.
   * @param limit - The maximum number of leads per page.
   * @param sort - Optional. The sorting criteria for the leads.
   * @param filter - Optional. The filtering criteria for the leads.
   * @returns A Promise that resolves to a PaginateResultDto containing the paginated leads.
   */
  public getDocumentationsPaginated(
    page: number,
    limit: number,
    sort?: string,
    filter?: string
  ): Observable<PaginateResultDto<DocumentationPopulatedResponseDto>> {
    let params = new HttpParams().set('page', page).set('limit', limit);

    if (sort) {
      params = params.set('sort', sort);
    }

    if (filter) {
      params = params.set('filter', filter);
    }

    return this.httpClient.get<PaginateResultDto<DocumentationPopulatedResponseDto>>(
      this.apiUrl,
      this.httpClient.buildResilienceConfigForTopic(DocumentationTopic.GET_DOCUMENTATIONS_PAGINATED),
      { params }
    );
  }

  /**
   * Returns a specific populated entity of type T referenced by id.
   * @param id the id of the enity to grab from the db.
   */
  public getCaregiversOfDependentWithDocumentationCount(dependentId: string): Promise<ICaregiverDocumentationCount[]> {
    return lastValueFrom(
      this.httpClient.get<ICaregiverDocumentationCount[]>(
        `${this.apiUrl}/stats/caregivers-of-dependent/${dependentId}`,
        this.httpClient.buildResilienceConfigForTopic(DocumentationTopic.GET_DOCUMENTATION_POPULATED)
      )
    );
  }
}
